import React from "react";
import { motion } from "framer-motion";
import { Droplets, Wheat, Flame, LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DailyStats } from "../../types";

interface MacroCardProps {
  label: string;
  value: number;
  total: number;
  unit: string;
  colorText: string;
  colorBar: string;
  icon: LucideIcon;
  delay: number;
}

const MacroCard: React.FC<MacroCardProps> = ({
  label,
  value,
  total,
  unit,
  colorText,
  colorBar,
  icon: Icon,
  delay,
}) => {
  const percent = Math.min(100, Math.max(0, (value / total) * 100));

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="rounded-3xl border border-line bg-surface p-3 shadow-card relative overflow-hidden"
    >
      <div className="flex flex-col items-center text-center">
        <div className={`flex h-8 w-8 items-center justify-center rounded-2xl bg-inset ${colorText}`}>
          <Icon size={16} />
        </div>
        <div className="mt-1 min-w-0">
          <span className="block whitespace-nowrap text-[9px] font-bold text-content-secondary uppercase tracking-[0.06em]">
            {label}
          </span>
          <span className="block text-base font-black text-content-primary leading-tight">
            {Math.round(value)}
            <span className="text-[10px] text-content-tertiary ml-0.5">{unit}</span>
          </span>
        </div>
      </div>

      <div className="w-full h-1.5 bg-inset rounded-full mt-2 overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percent}%` }}
          transition={{ duration: 1, delay: delay + 0.2 }}
          className={`h-full rounded-full ${colorBar}`}
        />
      </div>
    </motion.div>
  );
};

interface MacroCardsProps {
  stats: DailyStats;
}

/** Makro maqsadlar kunlik kaloriyadan hisoblanadi: 30% oqsil, 45% uglevod, 25% yog' */
const MacroCards: React.FC<MacroCardsProps> = ({ stats }) => {
  const { t } = useTranslation();

  const proteinTarget = Math.round((stats.dailyGoal * 0.3) / 4);
  const carbsTarget = Math.round((stats.dailyGoal * 0.45) / 4);
  const fatsTarget = Math.round((stats.dailyGoal * 0.25) / 9);

  return (
    <div className="px-4 grid grid-cols-3 gap-2">
      <MacroCard
        label={t("home.protein")}
        value={stats.totalProtein}
        total={proteinTarget}
        unit="g"
        colorText="text-info"
        colorBar="bg-info"
        icon={Droplets}
        delay={0.2}
      />
      <MacroCard
        label={t("home.carbs")}
        value={stats.totalCarbs}
        total={carbsTarget}
        unit="g"
        colorText="text-warning"
        colorBar="bg-warning"
        icon={Wheat}
        delay={0.3}
      />
      <MacroCard
        label={t("home.fats")}
        value={stats.totalFats}
        total={fatsTarget}
        unit="g"
        colorText="text-danger"
        colorBar="bg-danger"
        icon={Flame}
        delay={0.4}
      />
    </div>
  );
};

export default MacroCards;
